import type { DropdownFilterCallback } from '../../components/Dropdown/Dropdown';
import type {
  ApiKeyFormSubmitFn,
  DeleteApiKeyFn,
  GenerateApiKeySchemaFn,
  RegenerateApiKeyFn,
  ShowApiKeyHistoryFn,
  ShowApiKeyQrCodeFn,
} from '../../form/ApiKeyForm/ApiKeyForm';
import type { ContentTypeDefinition } from '../../lib/flotiq-client/types/api-response';
import type { UserLocalStorage } from '../../types/localStorage';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import {
  deleteApiKey,
  listContentTypes,
  postApiKey,
  regenerateApiKey,
  updateApiKey,
} from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import {
  generateApiDocs,
  getTestProps,
  parseScopedKeysDocs,
} from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import Panel from '../../components/Panel/Panel';
import Tooltip from '../../components/Tooltip/Tooltip';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import { useApiKeys, useContentTypes } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useSpaceConstraint } from '../../hooks/api/useSpaceConstraint';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { defaultTextRender, useModals } from '../../contexts/ModalContext';

import { QuestionMarkIcon } from '../../images/shapes';

import ApiKeyForm from '../../form/ApiKeyForm/ApiKeyForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import { KeyHistoryModalContent } from './modal/KeyHistoryModalContent';
import KeyRegenerateModalContent from './modal/KeyRegenerateModalContent';

const testId = 'api-keys';

const APIKeys = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const modal = useModals();
  const jwt = useToken();
  const { space, spaceLimits } = useSelectedSpace();
  const { reload: reloadConstraint } = useSpaceConstraint('scoped-keys-count');

  const { data: apiKeysData, isLoading, reload } = useApiKeys();
  const [newApiKeyForm, setNewApiKeyForm] = useState(false);
  const [loading, setLoading] = useState<string>();

  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');

  // :: Limits
  const scopedKeysLimit = spaceLimits?.scoped_keys_limit;
  const scopedKeysDocs = parseScopedKeysDocs(spaceLimits?.scoped_keys_docs);

  const apiScopedLen = useMemo(
    () =>
      apiKeysData.filter((el) => !el.global).length + (newApiKeyForm ? 1 : 0),
    [apiKeysData, newApiKeyForm],
  );

  const disabledAddScopedKeys =
    scopedKeysLimit !== -1 && scopedKeysLimit <= apiScopedLen;

  const ctdParams = useMemo(
    () => ({
      limit: 1000,
      order_by: 'label',
      order_direction: 'asc',
      page: 1,
    }),
    [],
  );

  const {
    data: contentTypes,
    isLoading: isLoadingCT,
    errors: ctdErrors,
  } = useContentTypes(ctdParams);

  const filterCtd = useCallback<DropdownFilterCallback>(
    async (query, _, setIsLoading) => {
      setIsLoading(true);
      let newOptions: ContentTypeDefinition[] = [];
      try {
        const { body, status, ok } = await listContentTypes(jwt, space, {
          ...ctdParams,
          name: query,
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        newOptions = body.data || [];
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'ContentForm.CouldntFetch'
              : 'Form.CommunicationErrorMessage',
          ),
        );
      }
      setIsLoading(false);
      return newOptions
        .filter((ctd) => !ctd.internal || ctd.name === '_media')
        .map((ctd) => ({ label: ctd.label, value: ctd.id }));
    },
    [jwt, ctdParams, t, space],
  );

  const contentTypesOptions = useMemo(() => {
    if (!contentTypes) return;

    return contentTypes
      .filter(
        (el) => !(el.internal && el.name !== '_tag' && el.name !== '_media'),
      )
      .map((el) => {
        return {
          value: el.id,
          label: el.label,
        };
      });
  }, [contentTypes]);

  useApiErrorsToast(ctdErrors);

  const handleAddApiKeys = useCallback(async () => {
    setNewApiKeyForm(true);
  }, []);

  const handleGetSchema = useCallback<GenerateApiKeySchemaFn>(
    async (name, apiKey, generateSchema) => {
      if (generateSchema) {
        const result = await modal.confirmation(
          t('ApiKeys.GetScopedSchemaDescription', { name: name }),
          t('Global.Warning'),
          t('ApiKeys.GetScopedSchemaConfirm'),
          t('ApiKeys.GetScopedSchemaCancel'),
          'warning',
          { size: 'lg' },
        );
        if (result) {
          TagManager.dataLayer({
            dataLayer: {
              event: 'apidocs_visit',
            },
          });
          window.open(
            generateApiDocs(
              {
                organization: user?.data?.organization,
              },
              { apiKey },
            ),
            '_blank',
          );
        }
      } else {
        window.open(getEnv('VITE_PRICING'), '_blank');
      }
    },
    [modal, user, t],
  );

  const handleRegenerate = useCallback<RegenerateApiKeyFn>(
    async (name, id) => {
      const keyName =
        apiKeysData.find((apiKey) => apiKey.id === id)?.name ?? name;

      const result = await modal({
        title: defaultTextRender.warning(t('Global.Warning')),
        content: (
          <KeyRegenerateModalContent
            expectedKeyName={keyName}
            testId={testId}
          />
        ),
        size: 'md',
        hideClose: false,
      });

      if (result) {
        setLoading(id);
        try {
          const { body, status, ok } = await regenerateApiKey(jwt, space, {
            id: id,
          });

          if (!ok) {
            throw parseResponseErrors(body, status);
          }
          queryClient.invalidateQueries({ queryKey: ['api-keys'] });
          toast.success(t('ApiKeys.RegenerateSuccess', { name: keyName }));
        } catch (error) {
          if (!(error instanceof ResponseError)) {
            toast.error(t('ApiKeys.DeleteModalError'));
          } else {
            toast.error(
              error.message
                ? `${t('ApiKeys.DeleteModalError')}. ${error.message}`
                : t('ApiKeys.DeleteModalError'),
            );
          }
        }
      }
      setLoading(undefined);
    },
    [modal, t, jwt, space, queryClient, apiKeysData],
  );

  const handleDelete = useCallback<DeleteApiKeyFn>(
    async (id, name, mode) => {
      if (mode === 'unsaved') {
        setNewApiKeyForm(false);
        return;
      }

      const result = await modal.delete(
        t('ApiKeys.DeleteDescription', { name: name }),
      );

      if (result) {
        try {
          setLoading(id);

          const { body, status, ok } = await deleteApiKey(jwt, space, {
            id: id,
          });
          if (!ok) {
            throw parseResponseErrors(body, status);
          }
          reloadConstraint();
          queryClient.invalidateQueries({ queryKey: ['api-keys'] });
          toast.success(t('ApiKeys.DeleteModalSuccess'));
        } catch (error) {
          if (!(error instanceof ResponseError)) {
            toast.error(t('ApiKeys.DeleteModalError'));
          } else {
            toast.error(
              error.message
                ? `${t('ApiKeys.DeleteModalError')}. ${error.message}`
                : t('ApiKeys.DeleteModalError'),
            );
          }
        }
      }

      setLoading(undefined);
    },
    [modal, t, jwt, space, reloadConstraint, queryClient],
  );

  const handleShowQRCode = useCallback<ShowApiKeyQrCodeFn>(
    (name, apiKey) => {
      modal.info(
        t('ApiKeys.YourApiKey', { name: name }),
        <div className="flex justify-center items-center pb-5">
          <QRCode
            size={300}
            value={`{"apiKey":"${apiKey}","apiUrl":"${getEnv('VITE_FLOTIQ_API_URL')}/api"}`}
            viewBox={`0 0 300 300`}
          />
        </div>,
        'qr-modal-api-key',
        { size: 'lg' },
      );
    },
    [modal, t],
  );

  const handleShowHistory = useCallback<ShowApiKeyHistoryFn>(
    (id, name: string, apiKey: string) => {
      modal({
        title: defaultTextRender.simple(t('ApiKeys.History.Title')),
        content: (
          <KeyHistoryModalContent
            id={id}
            name={name}
            apiKey={apiKey}
            {...getTestProps(testId, `${id}-history-modal`, 'testId')}
          />
        ),
        size: 'lg',
        contentAdditionalClasses: 'pt-0',
      });
    },
    [modal, t],
  );

  const handleSave = useCallback<ApiKeyFormSubmitFn>(
    async ({ value }) => {
      const { id, name, apiKey, permissions } = value;
      setLoading(id);

      const updateShape = {
        name: name,
        canCreate: false,
        canRead: false,
        canUpdate: false,
        canDelete: false,
        global: false,
        permissions: permissions.map((permission) => ({
          ...permission,
          contentTypeDefinition: { id: permission.contentTypeDefinition?.id },
        })),
      };

      if (apiKey) {
        try {
          const { body, status, ok } = await updateApiKey(jwt, space, {
            id: id,
            ...updateShape,
          });
          if (!ok) {
            throw parseResponseErrors(body, status);
          }

          reloadConstraint();
          queryClient.invalidateQueries({ queryKey: ['api-keys'] });
          toast.success(t('ApiKeys.SaveNewApiKeySuccess', { name: name }));
          setLoading(undefined);
        } catch {
          toast.error(t('ApiKeys.UpdateError'));
          setLoading(undefined);
        }
      } else {
        try {
          const { body, status, ok } = await postApiKey(
            jwt,
            space,
            updateShape,
          );
          if (!ok) {
            throw parseResponseErrors(body, status);
          }

          reloadConstraint();
          queryClient.invalidateQueries({ queryKey: ['api-keys'] });
          toast.success(t('ApiKeys.SaveNewApiKeySuccess', { name: name }));

          await reload();
          setLoading(undefined);
          setNewApiKeyForm(false);
        } catch {
          toast.error(t('ApiKeys.UpdateError'));
          setLoading(undefined);
        }
      }
    },
    [jwt, space, reloadConstraint, queryClient, t, reload],
  );

  return (
    <PageLayout
      page="apiKeys"
      title={t('Global.APIKeys')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        <TopbarButton
          label={t('ApiKeys.AccessDocumentation')}
          link={getEnv('VITE_DOCUMENTATION_API')}
          target="_blank"
          rel="noreferrer"
        />
      }
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withSidebar}>
        <div className={predefinedLayoutClasses.leftColumn}>
          {isLoading || isLoadingCT ? (
            <div className="h-full overflow-hidden flex justify-center items-center">
              <Loader type="spinner-grid" size="big" />
            </div>
          ) : (
            <>
              <Heading
                level={3}
                additionalClasses="pt-0 pb-0 mb-4 text-xl lg:text-3xl leading-none dark:text-slate-200"
              >
                {t('ApiKeys.ApplicationApiKeys')}
              </Heading>
              {apiKeysData?.map(
                (el) =>
                  el.global && (
                    <ApiKeyForm
                      key={el.id}
                      data={el}
                      rules={false}
                      generateSchema={true}
                      onGetSchema={handleGetSchema}
                      onRegenerate={handleRegenerate}
                      onDelete={handleDelete}
                      onShowQRCode={handleShowQRCode}
                      onShowHistory={handleShowHistory}
                      onSave={handleSave}
                      isLoading={el.id === loading}
                      {...getTestProps(testId, 'api-key-item-global', 'testId')}
                    />
                  ),
              )}
              <div className="my-4 border-t dark:border-slate-800 h-1 w-full" />
              <Heading
                level={3}
                additionalClasses="pt-0 pb-0 mb-4 text-xl lg:text-3xl leading-none dark:text-slate-200"
                {...getTestProps(testId, 'user-keys-header', 'testId')}
              >
                {t('ApiKeys.UserDefinedApiKeys')}
              </Heading>
              <div className="flex flex-row items-center">
                <Button
                  onClick={handleAddApiKeys}
                  additionalClasses={'mb-2'}
                  disabled={disabledAddScopedKeys}
                  {...getTestProps(testId, `button-add-apikey`)}
                >
                  {t('ApiKeys.AddNewApiKey')}
                </Button>

                {disabledAddScopedKeys && (
                  <Link
                    to={getEnv('VITE_PRICING') || ''}
                    target="_blank"
                    rel="noreferrer"
                    className={'ml-3'}
                    {...getTestProps(testId, `disabled-scoped`)}
                  >
                    <Tooltip
                      tooltip={t('ApiKeys.Limits')}
                      tooltipPlacement={'topCenter'}
                    >
                      <div
                        className={twMerge(
                          'w-4 h-4 flex justify-center',
                          'items-center rounded-full bg-red',
                        )}
                      >
                        <QuestionMarkIcon className="w-2 h-2 text-white" />
                      </div>
                    </Tooltip>
                  </Link>
                )}
              </div>

              {newApiKeyForm && (
                <ApiKeyForm
                  options={contentTypesOptions}
                  optionsFilterCallback={filterCtd}
                  editableName={true}
                  removable={true}
                  onDelete={handleDelete}
                  onSave={handleSave}
                  isLoading={loading === '_newApiKey'}
                  {...getTestProps(testId, 'api-key-item', 'testId')}
                />
              )}

              {apiKeysData?.map(
                (el) =>
                  !el.global && (
                    <ApiKeyForm
                      key={el.id}
                      data={el}
                      options={contentTypesOptions}
                      optionsFilterCallback={filterCtd}
                      editableName={true}
                      removable={true}
                      generateSchema={scopedKeysDocs}
                      onGetSchema={handleGetSchema}
                      onRegenerate={handleRegenerate}
                      onDelete={handleDelete}
                      onShowQRCode={handleShowQRCode}
                      onShowHistory={handleShowHistory}
                      onSave={handleSave}
                      isLoading={el.id === loading}
                      {...getTestProps(testId, 'api-key-item', 'testId')}
                    />
                  ),
              )}
            </>
          )}
        </div>

        <div className={predefinedLayoutClasses.rightColumn}>
          <Panel
            title={t('ApiKeys.ApplicationApiKeys')}
            isCollapsable={false}
            additionalContainerClasses="py-5 px-4"
            additionalTitleClasses="text-lg -mb-1"
            additionalChildrenClasses="dark:text-slate-200"
            open
          >
            <div className="flex flex-col space-y-3">
              {t('ApiKeys.ApplicationApiKeysDescription')}
            </div>
          </Panel>

          <Panel
            title={t('ApiKeys.UserDefinedApiKeys')}
            isCollapsable={false}
            additionalContainerClasses="py-5 px-4"
            additionalTitleClasses="text-lg -mb-1"
            additionalChildrenClasses="dark:text-slate-200"
            open
          >
            <div className="flex flex-col space-y-3">
              {t('ApiKeys.UserDefinedApiKeysDescription')}
            </div>
          </Panel>
        </div>
      </div>
    </PageLayout>
  );
};

export default APIKeys;
